/**
 * VendorPortalResponseService
 * Service for managing vendor responses to ratings.
 * Route: api/vendor-portal/responses
 */
import { VendorPortalBaseService } from './VendorPortalBaseService';
/**
 * VendorPortalResponseService
 * Handles vendor response CRUD operations for the vendor portal.
 * Maps to /api/vendor-portal/responses endpoint
 */
export class VendorPortalResponseService extends VendorPortalBaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? VendorPortalResponseService.DEFAULT_SERVICE_PATH,
            basePath: config?.basePath ?? VendorPortalResponseService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET ALL ====================
    /**
     * Get all responses created by the current vendor
     * GET /api/vendor-portal/responses
     */
    async getResponses() {
        const response = await this.getResponsesRaw();
        return response.data ?? [];
    }
    /**
     * Get all responses (raw response)
     */
    getResponsesRaw() {
        return this.get();
    }
    // ==================== GET BY ID ====================
    /**
     * Get a specific response by ID
     * GET /api/vendor-portal/responses/{id}
     */
    async getResponse(id) {
        const response = await this.getResponseRaw(id);
        if (!response.data) {
            throw new Error(`Response with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get a specific response (raw response)
     */
    getResponseRaw(id) {
        return this.get(String(id));
    }
    // ==================== GET BY RATING ====================
    /**
     * Get response for a specific rating
     * GET /api/vendor-portal/responses/rating/{ratingId}
     */
    async getResponseByRating(ratingId) {
        try {
            const response = await this.getResponseByRatingRaw(ratingId);
            return response.data ?? null;
        }
        catch {
            // Return null if not found (404)
            return null;
        }
    }
    /**
     * Get response by rating (raw response)
     */
    getResponseByRatingRaw(ratingId) {
        return this.get(`rating/${ratingId}`);
    }
    // ==================== CREATE ====================
    /**
     * Create a response to a rating
     * POST /api/vendor-portal/responses
     */
    async createResponse(request) {
        const response = await this.createResponseRaw(request);
        if (!response.data) {
            throw new Error('Failed to create response');
        }
        return response.data;
    }
    /**
     * Create a response (raw response)
     */
    createResponseRaw(request) {
        return this.post('', request);
    }
    // ==================== UPDATE ====================
    /**
     * Update an existing response
     * PUT /api/vendor-portal/responses/{id}
     */
    async updateResponse(id, request) {
        const response = await this.updateResponseRaw(id, request);
        if (!response.data) {
            throw new Error(`Failed to update response ${id}`);
        }
        return response.data;
    }
    /**
     * Update a response (raw response)
     */
    updateResponseRaw(id, request) {
        return this.put(String(id), request);
    }
    // ==================== DELETE ====================
    /**
     * Delete an existing response
     * DELETE /api/vendor-portal/responses/{id}
     */
    async deleteResponse(id) {
        const response = await this.deleteResponseRaw(id);
        return response.data ?? false;
    }
    /**
     * Delete a response (raw response)
     */
    deleteResponseRaw(id) {
        return this.delete(String(id));
    }
}
VendorPortalResponseService.DEFAULT_SERVICE_PATH = 'vendor-rating';
VendorPortalResponseService.BASE_PATH = 'api/vendor-portal/responses';
//# sourceMappingURL=VendorPortalResponseService.js.map